import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Sparkles, Loader2, ListChecks } from 'lucide-react';
import { Badge } from '../ui/badge';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SmartRuleBuilder
 *
 * Visual composer for Property Group `smart_rule` predicates. Users can
 * add / remove / edit predicates without touching JSON, and see a live
 * preview count of matched properties as they build.
 *
 * Rule shape (matches backend `_apply_smart_rule` in multi_tenant.py):
 *   { all: [ { field, op, value }, ... ] }   // every predicate must match
 */
export default function SmartRuleBuilder({ token, value, onChange, ruleFieldsCache }) {
  const [meta, setMeta] = useState(ruleFieldsCache || null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch fields metadata once
  useEffect(() => {
    if (meta) return;
    axios.get(`${BACKEND}/api/admin/property-groups/rule-fields`, { headers })
      .then(res => setMeta(res.data))
      .catch(() => setMeta({ fields: [], op_labels: {} }));
  }, [meta, headers]);

  const preds = (value && value.all) ? value.all : [];

  const setPreds = useCallback((next) => {
    onChange({ all: next });
  }, [onChange]);

  const addPred = () => {
    const firstField = meta?.fields?.[0];
    setPreds([...preds, { field: firstField?.key || 'brand', op: firstField?.ops?.[0] || 'eq', value: '' }]);
  };
  const updatePred = (i, patch) => {
    const next = preds.map((p, idx) => idx === i ? { ...p, ...patch } : p);
    setPreds(next);
  };
  const removePred = (i) => {
    setPreds(preds.filter((_, idx) => idx !== i));
  };

  // Live preview (debounced)
  useEffect(() => {
    if (!preds.length) { setPreview(null); return; }
    // Skip preview if any predicate has an empty value (avoids noisy "0 matches")
    const anyEmpty = preds.some(p => (p.value === '' || p.value == null) && !['in', 'not_in'].includes(p.op));
    if (anyEmpty) { setPreview(null); setPreviewErr(''); return; }
    setPreviewLoading(true);
    setPreviewErr('');
    const handle = setTimeout(() => {
      axios.post(`${BACKEND}/api/admin/property-groups/preview-rule`, { smart_rule: { all: preds } }, { headers })
        .then(res => setPreview(res.data))
        .catch(e => setPreviewErr(e?.response?.data?.detail || 'Preview failed'))
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [preds, headers]);

  return (
    <div className="space-y-3" data-testid="smart-rule-builder">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-500" />
        <div className="flex-1">
          <div className="text-sm font-semibold">Smart Rule Builder</div>
          <div className="text-[10px] text-muted-foreground">Matched properties auto-update as data changes. Every rule below must match (AND).</div>
        </div>
      </div>

      <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
        {preds.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-4" data-testid="smart-rule-empty">
            No rules yet. Add your first predicate below.
          </div>
        )}
        {preds.map((pred, i) => {
          const field = meta?.fields?.find(f => f.key === pred.field) || { ops: ['eq'], suggestions: [], type: 'string' };
          const useMulti = pred.op === 'in' || pred.op === 'not_in';
          return (
            <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border bg-card p-2" data-testid={`smart-rule-row-${i}`}>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 w-8">{i === 0 ? 'IF' : 'AND'}</span>

              {/* Field selector */}
              <Select
                value={pred.field}
                onValueChange={(v) => {
                  const nf = meta?.fields?.find(f => f.key === v);
                  updatePred(i, { field: v, op: nf?.ops?.[0] || 'eq', value: '' });
                }}
              >
                <SelectTrigger className="h-8 text-xs w-32" data-testid={`smart-rule-field-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.fields || []).map(f => (
                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Operator */}
              <Select value={pred.op} onValueChange={(v) => updatePred(i, { op: v, value: (v === 'in' || v === 'not_in') ? [] : '' })}>
                <SelectTrigger className="h-8 text-xs w-32" data-testid={`smart-rule-op-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(field.ops || []).map(op => (
                    <SelectItem key={op} value={op}>{meta?.op_labels?.[op] || op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Value editor */}
              <div className="flex-1 min-w-[140px]">
                {useMulti ? (
                  <MultiValueInput
                    field={field}
                    value={Array.isArray(pred.value) ? pred.value : []}
                    onChange={(v) => updatePred(i, { value: v })}
                    testId={`smart-rule-value-${i}`}
                  />
                ) : field.type === 'number' ? (
                  <Input
                    type="number"
                    className="h-8 text-xs"
                    value={pred.value ?? ''}
                    onChange={e => updatePred(i, { value: e.target.value === '' ? '' : Number(e.target.value) })}
                    placeholder="e.g. 150"
                    data-testid={`smart-rule-value-${i}`}
                  />
                ) : field.suggestions?.length ? (
                  <Select value={String(pred.value ?? '')} onValueChange={(v) => updatePred(i, { value: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`smart-rule-value-${i}`}><SelectValue placeholder="Choose value…" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {field.suggestions.map(s => (
                        <SelectItem key={String(s)} value={String(s)}>{String(s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="h-8 text-xs"
                    value={pred.value ?? ''}
                    onChange={e => updatePred(i, { value: e.target.value })}
                    placeholder="Value…"
                    data-testid={`smart-rule-value-${i}`}
                  />
                )}
              </div>

              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removePred(i)} data-testid={`smart-rule-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </div>
          );
        })}
        <Button type="button" variant="outline" size="sm" className="w-full h-8 gap-1.5 text-xs" onClick={addPred} data-testid="smart-rule-add">
          <Plus className="h-3.5 w-3.5" /> Add rule
        </Button>
      </div>

      {/* Live preview */}
      <div className="rounded-lg border p-3 bg-card flex items-start gap-3" data-testid="smart-rule-preview">
        <ListChecks className="h-4 w-4 text-violet-500 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold flex items-center gap-2">
            Live preview
            {previewLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
          {previewErr ? (
            <div className="text-[11px] text-rose-600 mt-0.5">{previewErr}</div>
          ) : preds.length === 0 ? (
            <div className="text-[11px] text-muted-foreground mt-0.5">Add a rule to see matched properties.</div>
          ) : preview ? (
            <>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                <strong className="text-foreground">{preview.matched_count}</strong> of <strong className="text-foreground">{preview.total_properties}</strong> properties match this rule.
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(preview.properties || []).slice(0, 10).map(p => (
                  <Badge key={p.id} variant="outline" className="text-[10px]" data-testid={`smart-rule-match-${p.id}`}>{p.name}</Badge>
                ))}
                {preview.matched_count > 10 && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">+{preview.matched_count - 10} more</Badge>
                )}
              </div>
            </>
          ) : (
            <div className="text-[11px] text-muted-foreground mt-0.5">Fill in all values to see matches.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MultiValueInput({ field, value, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const suggestions = field.suggestions || [];
  const remaining = suggestions.filter(s => !value.includes(String(s)) && !value.includes(s));

  const add = (v) => {
    const t = String(v).trim();
    if (!t) return;
    if (value.map(String).includes(t)) return;
    onChange([...value, field.type === 'number' ? Number(t) : t]);
    setDraft('');
  };
  const remove = (v) => onChange(value.filter(x => String(x) !== String(v)));

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 min-h-[32px] rounded-md border bg-background px-2 py-1">
        {value.map((v, i) => (
          <Badge key={i} variant="secondary" className="text-[10px] gap-1 pr-1">
            {String(v)}
            <button type="button" onClick={() => remove(v)} className="hover:text-destructive"><Trash2 className="h-2.5 w-2.5" /></button>
          </Badge>
        ))}
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); add(draft); }
            if (e.key === 'Backspace' && !draft && value.length) remove(value[value.length - 1]);
          }}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-xs"
          placeholder={value.length ? '' : 'Type + Enter'}
        />
      </div>
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {remaining.slice(0, 6).map(s => (
            <button
              key={String(s)}
              type="button"
              onClick={() => add(s)}
              className="text-[10px] px-1.5 py-0.5 rounded-md border bg-muted/40 hover:bg-muted"
            >
              + {String(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
